import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { DiscountDialog } from '@/features/discounts/discount-dialog'
import { errorMessage } from '@/lib/api/errors'
import type { CustomerDetail } from '@/lib/api/types'
import { formatDate, formatUsd } from '@/lib/format'
import { type CompDuration, type PlanAction, useComplimentaryAddons, usePlanAction } from './api'

/**
 * The customer's plan, and for owners and admins the changes the team can
 * make for them: the same rules as the customer's own (raising a plan applies
 * now and charges the card on file; lowering it waits for the renewal).
 */
export function PlanCard({ customer: c, canManage }: { customer: CustomerDetail; canManage: boolean }) {
  const pm = c.plan_management
  const sub = pm.subscription
  const action = usePlanAction(c.id)
  const paid = pm.plans.filter((p) => p.monthly > 0)
  const [changing, setChanging] = useState(false)
  const [plan, setPlan] = useState(sub?.plan ?? paid[0]?.key ?? '')
  const [interval, setInterval] = useState<'monthly' | 'annual'>(sub?.interval ?? 'monthly')
  const [comp, setComp] = useState(pm.complimentary_plan ?? '')
  const [duration, setDuration] = useState<CompDuration>('month')
  const [days, setDays] = useState('14')
  // "Tell the owner": a notice by email and in the app, unless staff untick it.
  const [tellOwner, setTellOwner] = useState(true)
  const ends = compEnd(duration, days)
  const [discount, setDiscount] = useState(false)
  const name = (key: string | null) => pm.plans.find((p) => p.key === key)?.name ?? key ?? '—'

  const run = (a: PlanAction, done: string) =>
    action.mutate(a, {
      onSuccess: () => {
        toast.success(done)
        setChanging(false)
      },
      onError: (e) => toast.error(errorMessage(e)),
    })

  return (
    <Card>
      <CardHeader
        title="Plan"
        description={
          canManage ? 'Changes are made in Lemon Squeezy and recorded in both audit logs.' : undefined
        }
      />
      <CardBody className="grid gap-4 text-[13px]">
        <div className="grid gap-1">
          <p className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-ink">{c.plan.name}</span>
            {pm.complimentary_plan && <Badge tone="neutral">Complimentary</Badge>}
            {sub?.ending && <Badge tone="warning">Ending</Badge>}
          </p>
          {sub ? (
            <p className="text-ink-muted">
              {name(sub.plan)}, {sub.interval} ·{' '}
              {sub.ending ? `ends ${formatDate(sub.ends_at)}` : `renews ${formatDate(sub.renews_at)}`}
            </p>
          ) : (
            <p className="text-ink-muted">No subscription.</p>
          )}
          {sub?.scheduled && (
            <p className="text-warning">
              Moving to {name(sub.scheduled.plan)} ({sub.scheduled.interval}) at the renewal on{' '}
              {formatDate(sub.renews_at)}.
            </p>
          )}
        </div>

        {canManage && (
          <div className="flex flex-wrap gap-2">
            {sub && !sub.ending && pm.configured && (
              <Button size="sm" variant="secondary" onClick={() => setChanging(true)}>
                Change plan
              </Button>
            )}
            {sub?.scheduled && (
              <Button
                size="sm"
                variant="secondary"
                loading={action.isPending}
                onClick={() => run({ action: 'keep-plan' }, 'They keep their current plan.')}
              >
                Keep current plan
              </Button>
            )}
            {sub && !sub.ending && (
              <Button
                size="sm"
                variant="ghost"
                loading={action.isPending}
                onClick={() => run({ action: 'cancel' }, 'Cancelled at the end of the paid period.')}
              >
                Cancel at period end
              </Button>
            )}
            {sub?.ending && (
              <Button
                size="sm"
                variant="secondary"
                loading={action.isPending}
                onClick={() => run({ action: 'resume' }, 'The plan carries on.')}
              >
                Resume plan
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setDiscount(true)}>
              Create a discount for them
            </Button>
          </div>
        )}

        {canManage && (
          <div className="grid gap-2 border-t border-border pt-4">
            <Field
              label="Complimentary plan"
              htmlFor="comp-plan"
              hint="Given by Arva SEO at no charge, on top of any subscription, for as long as you choose. When it ends, the workspace’s own plan and limits apply again and the owner is told (and reminded 3 days before)."
            >
              <div className="grid gap-2">
                <NativeSelect id="comp-plan" value={comp} onChange={(e) => setComp(e.target.value)}>
                  <option value="">None</option>
                  {paid.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.name}
                    </option>
                  ))}
                </NativeSelect>
                {comp !== '' && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="w-40">
                      <NativeSelect
                        aria-label="How long it lasts"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value as CompDuration)}
                      >
                        <option value="day">1 day</option>
                        <option value="week">1 week</option>
                        <option value="month">1 month</option>
                        <option value="year">1 year</option>
                        <option value="custom">Custom</option>
                      </NativeSelect>
                    </span>
                    {duration === 'custom' && (
                      <span className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          max={3650}
                          aria-label="Number of days"
                          className="w-24"
                          value={days}
                          onChange={(e) => setDays(e.target.value)}
                        />
                        days
                      </span>
                    )}
                    {ends && (
                      <span className="text-xs text-ink-subtle">Ends {formatDate(ends.toISOString())}</span>
                    )}
                  </div>
                )}
                {(comp !== '' || pm.complimentary_plan) && (
                  <TellOwner checked={tellOwner} onChange={setTellOwner} />
                )}
                {(comp !== '' || pm.complimentary_plan) && (
                  <Button
                    variant="secondary"
                    className="w-fit"
                    loading={action.isPending}
                    disabled={comp === '' ? !pm.complimentary_plan : !ends}
                    onClick={() =>
                      action.mutate(
                        comp === ''
                          ? { action: 'complimentary', plan: null, notify: tellOwner }
                          : {
                              action: 'complimentary',
                              plan: comp,
                              duration,
                              days: duration === 'custom' ? Number(days) : undefined,
                              notify: tellOwner,
                            },
                        {
                          onSuccess: (updated) =>
                            toast.success(
                              (comp === ''
                                ? 'Complimentary plan removed.'
                                : `${name(comp)} given at no charge until ${formatDate(updated.plan_management.complimentary_until)}.`) +
                                (tellOwner ? ' The owner is told.' : ''),
                            ),
                          onError: (e) => toast.error(errorMessage(e)),
                        },
                      )
                    }
                  >
                    {comp === ''
                      ? 'Remove complimentary plan'
                      : pm.complimentary_plan
                        ? 'Save new end date'
                        : 'Give plan'}
                  </Button>
                )}
              </div>
            </Field>
            {pm.complimentary_plan && (
              <p className="text-xs text-ink-muted">
                {name(pm.complimentary_plan)} is complimentary{' '}
                {pm.complimentary_until
                  ? `until ${formatDate(pm.complimentary_until)}.`
                  : 'with no end date (given before end dates existed). Set one above.'}
              </p>
            )}
            {pm.complimentary_plan && pm.addons && pm.addons.length > 0 && (
              <ComplimentaryAddons
                key={JSON.stringify(pm.complimentary_addons)}
                customerId={c.id}
                addons={pm.addons}
                given={pm.complimentary_addons}
                until={pm.complimentary_until}
              />
            )}
          </div>
        )}
      </CardBody>

      <Dialog open={changing} onOpenChange={setChanging}>
        <DialogContent
          title={`Change ${c.name}’s plan`}
          description="A higher plan (or monthly to annual) applies now and the difference is charged to their card. A lower plan waits for the renewal. Tell the customer before you change it."
        >
          <div className="grid gap-4">
            <Field label="Plan" htmlFor="new-plan">
              <NativeSelect id="new-plan" value={plan} onChange={(e) => setPlan(e.target.value)}>
                {paid.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name} · {formatUsd(p.monthly)} a month or {formatUsd(p.annual)} a year
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Billing" htmlFor="new-interval">
              <NativeSelect
                id="new-interval"
                value={interval}
                onChange={(e) => setInterval(e.target.value as 'monthly' | 'annual')}
              >
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </NativeSelect>
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              loading={action.isPending}
              disabled={sub?.plan === plan && sub.interval === interval}
              onClick={() => run({ action: 'plan', plan, interval }, 'Plan changed.')}
            >
              Change plan
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {discount && <DiscountDialog open onOpenChange={setDiscount} customer={{ id: c.id, name: c.name }} />}
    </Card>
  )
}

/** When a complimentary plan given now would end (the API works it out the same way). */
function compEnd(duration: CompDuration, days: string): Date | null {
  const d = new Date()
  if (duration === 'day') d.setDate(d.getDate() + 1)
  else if (duration === 'week') d.setDate(d.getDate() + 7)
  else if (duration === 'year') d.setFullYear(d.getFullYear() + 1)
  else if (duration === 'month') {
    const day = d.getDate()
    d.setDate(1)
    d.setMonth(d.getMonth() + 1)
    d.setDate(Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()))
  } else {
    const n = Number(days)
    if (!Number.isInteger(n) || n < 1 || n > 3650) return null
    d.setDate(d.getDate() + n)
  }
  return d
}

/**
 * Free add-on units on top of the complimentary plan (and of any the customer buys). They end with
 * the complimentary plan.
 */
function ComplimentaryAddons({
  customerId,
  addons,
  given,
  until,
}: {
  customerId: string
  addons: NonNullable<CustomerDetail['plan_management']['addons']>
  given: Record<string, number>
  until: string | null
}) {
  const save = useComplimentaryAddons(customerId)
  const [tellOwner, setTellOwner] = useState(true)
  const [units, setUnits] = useState<Record<string, string>>(() =>
    Object.fromEntries(addons.map((a) => [a.code, String(given[a.code] ?? 0)])),
  )
  const chosen = Object.fromEntries(
    Object.entries(units).map(([code, v]) => [code, Math.max(0, Math.min(20, Math.floor(Number(v) || 0)))]),
  )
  const changed = addons.some((a) => (chosen[a.code] ?? 0) !== (given[a.code] ?? 0))

  return (
    <div className="grid gap-2 rounded-md border border-border p-3">
      <div className="grid gap-0.5">
        <p className="text-[13px] font-medium text-ink">Complimentary add-ons</p>
        <p className="text-xs text-ink-muted">
          Free extra capacity with the complimentary plan, on top of any add-ons they buy. They end with the
          plan
          {until ? ` on ${formatDate(until)}` : ''}.
        </p>
      </div>
      <ul className="grid gap-1.5 sm:grid-cols-2">
        {addons.map((a) => (
          <li key={a.code} className="flex items-center justify-between gap-2 text-xs">
            <label htmlFor={`comp-addon-${a.code}`} className="min-w-0 text-ink-muted">
              {a.name} <span className="text-ink-subtle">(+{a.unit_capacity.toLocaleString()} each)</span>
            </label>
            <Input
              id={`comp-addon-${a.code}`}
              type="number"
              min={0}
              max={20}
              className="h-8 w-16 text-xs"
              value={units[a.code] ?? '0'}
              onChange={(e) => setUnits((u) => ({ ...u, [a.code]: e.target.value }))}
            />
          </li>
        ))}
      </ul>
      <TellOwner checked={tellOwner} onChange={setTellOwner} />
      <Button
        size="sm"
        variant="secondary"
        className="w-fit"
        disabled={!changed}
        loading={save.isPending}
        onClick={() =>
          save.mutate(
            { addons: chosen, notify: tellOwner },
            {
              onSuccess: () =>
                toast.success(`Complimentary add-ons saved.${tellOwner ? ' The owner is told.' : ''}`),
              onError: (e) => toast.error(errorMessage(e)),
            },
          )
        }
      >
        Save add-ons
      </Button>
    </div>
  )
}

/** Whether the owner gets a notice (email and in the app) about this change. Ticked by default. */
function TellOwner({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="flex items-start gap-2 text-xs text-ink">
      <input
        type="checkbox"
        className="mt-0.5 size-4 accent-[var(--color-accent)]"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        Tell the owner
        <span className="block text-ink-subtle">
          A notice by email and in the app. Untick to change it quietly.
        </span>
      </span>
    </label>
  )
}
